import * as Tone from "tone";
import { GAME_DURATION } from "../constants";
import { AUDIO_FEEL_CONFIG } from "../audioFeelConfig";
import { EmotionalAudioSystem, type EmotionalAudioUpdate } from "./EmotionalAudioSystem";

// ─── Intensity Timeline ───────────────────────────────────────────────────────
const TIMELINE = [
  { from: 0,   to: 15,  intensity: 0.18, label: "intro"     },
  { from: 15,  to: 40,  intensity: 0.36, label: "pursuit"   },
  { from: 40,  to: 70,  intensity: 0.58, label: "maze"      },
  { from: 70,  to: 95,  intensity: 0.68, label: "swamp-fog" },
  { from: 95,  to: 115, intensity: 0.9,  label: "climax"    },
  { from: 115, to: 120, intensity: 1,    label: "escape"    }
];

// ─── AudioSyncSystem ──────────────────────────────────────────────────────────
/**
 * Owns the full audio graph:
 *   - Tone.Transport (clock for beat scheduling)
 *   - Tone.Player    (music track playback — unified AudioContext with the rest)
 *   - EmotionalAudioSystem (procedural layers: breath, drone, heartbeat, etc.)
 *
 * Previously used Howler.js for the music file. That second AudioContext has
 * been removed; everything now shares the single Tone.js AudioContext.
 *
 * To swap the music file, update AUDIO_FEEL_CONFIG.musicUrl.
 */
export class AudioSyncSystem {
  private player: Tone.Player;
  private startedAt = 0;
  private pausedAt = 0;
  private pausedTotal = 0;
  private running = false;
  private muted = false;
  private musicEnergyRatio: number = AUDIO_FEEL_CONFIG.songEnergyStart;
  private emotional = new EmotionalAudioSystem();

  elapsed = 0;
  amplitude = 0;
  beat = 0;
  segment = TIMELINE[0];

  constructor() {
    // Create the player immediately so it is available before start().
    // It won't emit sound until start() is called.
    this.player = new Tone.Player({
      url: AUDIO_FEEL_CONFIG.musicUrl,
      loop: AUDIO_FEEL_CONFIG.musicLoop,
      volume: this.getMusicTargetDb()
    }).toDestination();
  }

  async start() {
    // Tone.start() resumes the shared AudioContext (required after user gesture).
    await Tone.start();
    this.stop();

    // ── Beat clock ───────────────────────────────────────────────────────────
    Tone.getTransport().cancel();
    Tone.getTransport().bpm.value = 130;
    Tone.getTransport().scheduleRepeat(() => {
      this.beat = Math.min(1, this.beat + 0.32);
    }, "4n", 0);
    Tone.getTransport().start();

    // ── Music playback ───────────────────────────────────────────────────────
    // Wait for the buffer to be ready in case it hasn't finished loading yet.
    await Tone.loaded();
    this.player.seek(0);
    this.player.volume.value = this.getMusicTargetDb();
    this.player.start();

    // ── Procedural audio ─────────────────────────────────────────────────────
    this.startedAt = performance.now();
    this.pausedTotal = 0;
    this.elapsed = 0;
    this.running = true;
    await this.emotional.start();
  }

  pause() {
    if (!this.running) return;
    this.running = false;
    this.pausedAt = performance.now();
    this.player.stop();
    Tone.getTransport().pause();
    this.emotional.pause();
  }

  resume() {
    if (this.running) return;
    this.running = true;
    const gap = performance.now() - this.pausedAt;
    this.pausedTotal += gap;
    // Defensive: after backgrounding (screen lock / app switch), the browser may
    // have suspended the shared AudioContext. CONTINUAR is a user gesture, so we
    // can re-resume it here; otherwise Transport/player restart silently and no
    // sound returns. Safe on Android (no-op when already running). Fire-and-forget
    // — the rampTo/seek below still apply once the context is live again.
    const context = Tone.getContext().rawContext;
    if (context.state === "suspended") void context.resume();
    // Restart the music from where it paused
    const seekTo = Math.min(this.elapsed, GAME_DURATION - 0.05);
    this.player.start(Tone.now(), seekTo);
    Tone.getTransport().start();
    this.emotional.resume();
  }

  stop() {
    this.running = false;
    if (this.player.state === "started") this.player.stop();
    Tone.getTransport().stop();
    this.emotional.stop();
  }

  update(dt: number) {
    if (this.running) {
      this.elapsed = Math.min(GAME_DURATION, (performance.now() - this.startedAt - this.pausedTotal) / 1000);
    }
    this.segment = TIMELINE.find((part) => this.elapsed >= part.from && this.elapsed < part.to) ?? TIMELINE[TIMELINE.length - 1];
    const pulse = (Math.sin(this.elapsed * Math.PI * 3.6) + 1) * 0.5;
    const micro = (Math.sin(this.elapsed * Math.PI * 7.2 + 0.6) + 1) * 0.5;
    this.beat = Math.max(0, this.beat - dt * 1.8);
    this.amplitude = Math.min(1, this.segment.intensity * (0.45 + pulse * 0.38 + micro * 0.17) + this.beat * 0.24);
  }

  updateEmotion(payload: EmotionalAudioUpdate) {
    this.emotional.update(payload);
  }

  playPickup(energy: number) {
    this.emotional.playPickup(energy);
  }

  playChampionCharge() {
    this.emotional.playChampionCharge();
  }

  playLose() {
    this.emotional.playLose();
  }

  playWin() {
    this.emotional.playWin();
  }

  setMuted(muted: boolean) {
    // Mute/unmute the music track and the procedural layer simultaneously.
    this.muted = muted;
    this.player.volume.rampTo(this.getMusicTargetDb(), 0.08);
    this.emotional.setMuted(muted);
  }

  setMusicEnergy(ratio: number, immediate = false) {
    this.musicEnergyRatio = Math.max(0, Math.min(1, ratio));
    const targetDb = this.getMusicTargetDb();
    if (immediate) this.player.volume.value = targetDb;
    else this.player.volume.rampTo(targetDb, 0.16);
  }

  get progress() {
    return Math.min(1, this.elapsed / GAME_DURATION);
  }

  getSongTime() {
    return this.elapsed;
  }

  private getMusicTargetDb() {
    if (this.muted) return -Infinity;
    if (this.musicEnergyRatio <= AUDIO_FEEL_CONFIG.songEnergyAudibleThreshold) return AUDIO_FEEL_CONFIG.musicSilentDb;
    const audible = (this.musicEnergyRatio - AUDIO_FEEL_CONFIG.songEnergyAudibleThreshold) / (1 - AUDIO_FEEL_CONFIG.songEnergyAudibleThreshold);
    const shaped = Math.pow(Math.max(0, Math.min(1, audible)), 0.72);
    return AUDIO_FEEL_CONFIG.musicSilentDb + (AUDIO_FEEL_CONFIG.musicVolumeDb - AUDIO_FEEL_CONFIG.musicSilentDb) * shaped;
  }
}
